// 004 (FR-042, FR-043; research R8, R9; data-model §7): Add student saves the student first, then
// the new address (when one is needed). A saved part is never sent again; a failed part is retried
// with the same Idempotency-Key, which is replaced only when that part's payload changes.
import type { AppError } from "@/api/errors";
import type { Api } from "@/api/types";

type Part = { saved: unknown | null; key: string; body: string | null };

export type CombinedResult =
  | { ok: true; student: Api["Student"]; address: Api["Address"] | null }
  | {
      ok: false;
      part: "student" | "address";
      error: AppError;
      studentSaved: boolean;
    };

export function createCombinedSave(deps: {
  addStudent: (
    body: Api["StudentCreate"],
    key: string,
  ) => Promise<Api["Student"]>;
  addAddress: (
    body: Api["AddressCreate"],
    key: string,
  ) => Promise<Api["Address"]>;
  newKey: () => string;
  onStudentSaved?: (student: Api["Student"]) => void;
}) {
  const student: Part = {
    saved: null,
    key: deps.newKey(),
    body: null,
  };
  const address: Part = {
    saved: null,
    key: deps.newKey(),
    body: null,
  };

  function keyFor<B>(part: Part, body: B): string {
    const json = JSON.stringify(body);
    if (part.body !== null && part.body !== json) part.key = deps.newKey();
    part.body = json;
    return part.key;
  }

  return {
    get studentSaved(): boolean {
      return student.saved !== null;
    },
    async submit(
      studentBody: Api["StudentCreate"],
      addressBody: Api["AddressCreate"] | null,
    ): Promise<CombinedResult> {
      if (student.saved === null) {
        try {
          const saved = await deps.addStudent(
            studentBody,
            keyFor(student, studentBody),
          );
          student.saved = saved;
          deps.onStudentSaved?.(saved);
        } catch (error) {
          return {
            ok: false,
            part: "student",
            error: error as AppError,
            studentSaved: false,
          };
        }
      }
      if (addressBody !== null && address.saved === null) {
        try {
          address.saved = await deps.addAddress(
            addressBody,
            keyFor(address, addressBody),
          );
        } catch (error) {
          return {
            ok: false,
            part: "address",
            error: error as AppError,
            studentSaved: true,
          };
        }
      }
      return {
        ok: true,
        student: student.saved as Api["Student"],
        address: (address.saved as Api["Address"] | null) ?? null,
      };
    },
  };
}
